import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Globe, Truck, ShieldCheck } from "lucide-react";
import Hero from "@/components/Hero";
import ProductCard from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";
import { useApp } from "@/context/AppContext";
import { fetchProducts } from "@/api";
import { buildRecommendations } from "@/lib/browsingHistory";
import Seo, { SITE_URL } from "@/components/Seo";
import { ProductGridSkeleton, ProductsError } from "@/components/ProductsState";

export default function Home() {
  const { t } = useApp();
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    fetchProducts({})
      .then((all) => setFeatured(buildRecommendations(all, 8)))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const orgLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "BuyHeda",
    url: SITE_URL,
    logo: `${SITE_URL}/og-image.jpg`,
    sameAs: [
      "https://www.instagram.com/gelogaeva_gold/",
      "https://t.me/s/gelogaevaheda",
      "https://t.me/s/hedageogaeva",
    ],
  };
  const websiteLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "BuyHeda",
    url: SITE_URL,
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/catalogue?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };

  const trust = [
    { icon: Globe, label: t.home.trust.worldwide },
    { icon: Truck, label: t.home.trust.shippingIncluded },
    { icon: ShieldCheck, label: t.home.trust.couriers },
  ];

  return (
    <div data-testid="home-page">
      <Seo title={t.seo.home.title} description={t.seo.home.description} canonicalPath="/" jsonLd={[orgLd, websiteLd]} />
      <Hero />

      {/* Trust strip */}
      <section className="mt-14 md:mt-20 border-y border-line" data-testid="trust-strip">
        <div className="px-5 md:px-10 py-4 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 md:gap-x-14">
          {trust.map(({ icon: Icon, label }, i) => (
            <div key={i} className="flex items-center gap-2.5 text-[11px] uppercase tracking-[0.16em] text-ink/60" data-testid={`trust-item-${i}`}>
              <Icon className="h-4 w-4 text-accent" strokeWidth={1.5} />
              {label}
            </div>
          ))}
        </div>
      </section>

      {/* Featured selection */}
      <section className="px-5 md:px-10 py-24 md:py-32" data-testid="featured-section">
        <div className="flex items-end justify-between mb-14 flex-wrap gap-6">
          <div>
            <Reveal><p className="text-xs uppercase tracking-[0.28em] text-muted mb-4">{t.catalogue.title}</p></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display text-6xl md:text-8xl tracking-tight leading-[0.85]">
                {t.catalogue.title === "Каталог" ? "Избранное" : "Selected"}<br />
                <span className="text-accent">{t.catalogue.title === "Каталог" ? "сезона" : "for you"}</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <Link to="/catalogue" data-testid="featured-shop-link" className="group inline-flex items-center gap-3 text-xs uppercase tracking-[0.2em] link-underline">
              {t.nav.catalogue} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        {loading ? (
          <ProductGridSkeleton count={8} />
        ) : error ? (
          <ProductsError onRetry={load} />
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-12 md:gap-x-6 md:gap-y-16">
            {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        )}
      </section>
    </div>
  );
}
